// Suspension banner — shown when account is paused, throttled, or terminated.
// Clicking "Learn more" opens a modal with details.

import { useState } from 'react'
import { useAuth } from '@/lib/auth-context'
import { SuspensionModal } from '@/components/suspension-modal'
import { humanizeReason } from '@/lib/suspension-utils'

export function SuspensionBanner(): React.JSX.Element | null {
  const { account } = useAuth()
  const [modalOpen, setModalOpen] = useState(false)

  if (!account) return null

  const level = account.suspension_level ?? 0
  if (level === 0 && !account.sending_paused) return null

  const summary = humanizeReason(account.pause_reason)

  // Level 1: Warning/throttle (yellow banner)
  if (level === 1) {
    return (
      <>
        <div className="border-b border-warning/30 bg-warning/10 px-4 py-3">
          <div className="mx-auto flex max-w-5xl items-center gap-3">
            <span className="text-sm font-medium text-warning-700">
              Sending throttled to {account.throttle_percent ?? 50}%
            </span>
            <span className="text-sm text-text-secondary">{summary}</span>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="ml-auto text-sm font-medium text-brand-500 hover:text-brand-600"
            >
              Learn more
            </button>
          </div>
        </div>
        <SuspensionModal open={modalOpen} onClose={() => setModalOpen(false)} account={account} />
      </>
    )
  }

  // Level 2: Suspended (red banner with expiry)
  if (level === 2) {
    const expiresText = account.suspension_expires_at
      ? `Resumes ${new Date(account.suspension_expires_at).toLocaleDateString()}`
      : 'Temporarily paused'
    return (
      <>
        <div className="border-b border-danger/30 bg-danger/10 px-4 py-3">
          <div className="mx-auto flex max-w-5xl items-center gap-3">
            <span className="text-sm font-medium text-danger">Sending paused</span>
            <span className="text-sm text-text-secondary">
              {summary}. {expiresText}.
            </span>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="ml-auto rounded-lg bg-danger px-3 py-1.5 text-sm font-medium text-white hover:bg-red-600"
            >
              Learn more
            </button>
          </div>
        </div>
        <SuspensionModal open={modalOpen} onClose={() => setModalOpen(false)} account={account} />
      </>
    )
  }

  // Level 3: Under review (red banner, no expiry)
  if (level === 3) {
    return (
      <>
        <div className="border-b border-danger/30 bg-danger/10 px-4 py-3">
          <div className="mx-auto flex max-w-5xl items-center gap-3">
            <span className="text-sm font-medium text-danger">Sending paused — under review</span>
            <span className="text-sm text-text-secondary">{summary}</span>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="ml-auto rounded-lg bg-danger px-3 py-1.5 text-sm font-medium text-white hover:bg-red-600"
            >
              Learn more
            </button>
          </div>
        </div>
        <SuspensionModal open={modalOpen} onClose={() => setModalOpen(false)} account={account} />
      </>
    )
  }

  // Level 4: Terminated (dark banner)
  if (level === 4) {
    return (
      <>
        <div className="border-b border-gray-800 bg-gray-900 px-4 py-3">
          <div className="mx-auto flex max-w-5xl items-center gap-3">
            <span className="text-sm font-medium text-white">Account terminated</span>
            <span className="text-sm text-gray-400">{summary}</span>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="ml-auto text-sm font-medium text-gray-300 hover:text-white"
            >
              Learn more
            </button>
          </div>
        </div>
        <SuspensionModal open={modalOpen} onClose={() => setModalOpen(false)} account={account} />
      </>
    )
  }

  // Fallback: generic pause (old-style sending_paused without level)
  if (account.sending_paused) {
    return (
      <div className="border-b border-danger/30 bg-danger/10 px-4 py-3">
        <div className="mx-auto flex max-w-5xl items-center gap-3">
          <span className="text-sm font-medium text-danger">Sending paused</span>
          <span className="text-sm text-text-secondary">{summary}</span>
        </div>
      </div>
    )
  }

  return null
}
